<template>
    <section v-if="hasIssuerGlanceContent" class="issuer-glance">
        <h2 class="issuer-glance__heading">
            <button
                type="button"
                class="issuer-glance__header"
                :aria-expanded="isOpen"
                aria-controls="issuer-glance-content"
                @click="isOpen = !isOpen"
            >
                <span id="issuer-glance-title" class="issuer-glance__title">{{ data.issuerGlance.title }}</span>
                <i class="mdi" :class="isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'" aria-hidden="true"></i>
            </button>
        </h2>

        <BondV2SignupBlurGate
            v-if="isOpen"
            id="issuer-glance-content"
            :message="issuerGlanceGateMessage"
            @signup-required="handleSignupGate"
        >
            <div class="issuer-glance__body">
                <div v-if="metricItems.length" class="glance-grid" role="list" aria-label="Issuer financial snapshot">
                    <article v-for="metric in metricItems" :key="metric.label" class="glance-tile" role="listitem">
                        <div v-if="metric.icon" class="glance-tile__icon">
                            <img :src="metric.icon" alt="" aria-hidden="true" />
                        </div>
                        <strong>{{ metric.value }}</strong>
                        <span>
                            {{ metric.label }}
                            <VTooltip
                                v-if="metric.tooltip"
                                :text="metric.tooltip"
                                location="top"
                                class="issuer-glance-tooltip"
                            >
                                <template #activator="{ props }">
                                    <button
                                        v-bind="props"
                                        type="button"
                                        class="glance-info-icon-wrapper"
                                        :aria-label="`More information about ${metric.label}: ${metric.tooltip}`"
                                    >
                                        <VIcon class="glance-tile__info-icon" size="14" aria-hidden="true">
                                            mdi-information-outline
                                        </VIcon>
                                    </button>
                                </template>
                            </VTooltip>
                        </span>
                    </article>
                </div>

                <div v-if="selectedTrendConfig" class="trend" aria-labelledby="financial-trend-title">
                    <h3 id="financial-trend-title" class="trend__title">{{ trendTitle }}</h3>
                    <div class="trend-tabs" role="tablist" aria-label="Financial trend metrics">
                        <button
                            v-for="(tab, index) in trendTabs"
                            :key="tab"
                            :id="trendTabId(tab)"
                            type="button"
                            role="tab"
                            :class="{ active: tab === selectedTrendTab }"
                            :aria-selected="tab === selectedTrendTab"
                            :aria-controls="trendPanelId(tab)"
                            :tabindex="tab === selectedTrendTab ? 0 : -1"
                            @click="selectedTrendTab = tab"
                            @keydown="handleTrendTabKeydown($event, index)"
                        >
                            {{ tab }}
                        </button>
                    </div>
                    <div
                        v-if="selectedTrendTab"
                        :id="trendPanelId(selectedTrendTab)"
                        class="trend__tabpanel"
                        role="tabpanel"
                        :aria-labelledby="trendTabId(selectedTrendTab)"
                    >
                        <p
                            v-if="selectedTrendConfig.change"
                            class="trend__change"
                            :class="`trend__change--${selectedTrendChangeTone}`"
                        >
                            {{ selectedTrendConfig.change }}
                        </p>
                        <div
                            v-if="selectedTrendConfig.type !== 'doughnut-grid'"
                            class="chart"
                            :class="`chart--${selectedTrendConfig.type}`"
                            role="img"
                            :aria-label="trendChartAriaLabel"
                        >
                            <canvas ref="trendChartCanvas" role="img" :aria-label="trendChartAriaLabel">
                                {{ trendChartAriaLabel }}
                            </canvas>
                        </div>
                        <div v-else class="doughnut-grid" role="list" aria-label="Borrowing mix doughnut charts">
                            <article v-for="(chart, index) in selectedDoughnutCharts" :key="chart.title" class="doughnut-card" role="listitem">
                                <h4 class="doughnut-card__title">
                                    {{ chart.title }}
                                    <VTooltip
                                        :text="chart.tooltip"
                                        location="top"
                                        class="issuer-glance-tooltip"
                                        :aria-label="`${chart.title} information tooltip`"
                                    >
                                        <template #activator="{ props }">
                                            <button
                                                v-bind="props"
                                                type="button"
                                                class="doughnut-card__info-button"
                                                :aria-label="`More information about ${chart.title}: ${chart.tooltip}`"
                                            >
                                                <img :src="borrowingMixInfoIcon" alt="" aria-hidden="true" />
                                            </button>
                                        </template>
                                    </VTooltip>
                                </h4>
                                <div class="doughnut-card__chart">
                                    <canvas
                                        :ref="(element) => setDoughnutCanvas(element, index)"
                                        role="img"
                                        :aria-label="doughnutChartAriaLabel(chart)"
                                    >
                                        {{ doughnutChartAriaLabel(chart) }}
                                    </canvas>
                                </div>
                                <div class="trend-legend" role="list" :aria-label="`${chart.title} legend`">
                                    <span v-for="item in chart.legend" :key="item.label" role="listitem">
                                        <i :style="{ background: item.color }" aria-hidden="true"></i>
                                        {{ item.label }}
                                    </span>
                                </div>
                            </article>
                        </div>
                    </div>
                </div>
            </div>
        </BondV2SignupBlurGate>
    </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Chart from 'chart.js/auto'
import borrowingMixInfoIcon from '@/assets/icons/bond-details-v2/borrowing-mix-info.svg'
import BondV2SignupBlurGate from './BondV2SignupBlurGate.vue'
import type { BondV2FinancialTrendConfig } from './bondV2Data'
import { useBondV2Data } from './bondV2Context'

const isOpen = ref(true)
const trendChartCanvas = ref<HTMLCanvasElement | null>(null)
const trendDoughnutCanvases = ref<HTMLCanvasElement[]>([])
let trendCharts: Chart[] = []
const selectedTrendTab = ref('')
const data = useBondV2Data()
const emit = defineEmits<{
    'signup-required': [payload: SignupGatePayload]
}>()

interface SignupGatePayload {
    title: string
    message: string
    ctaName: string
}

const metricItems = computed(() => data.value.issuerGlance.metrics.map((metric) => ({
    ...metric,
    icon: metric.icon || ''
})))

const financialTrend = computed(() => data.value.issuerGlance.financialTrend)
const trendTitle = computed(() => financialTrend.value?.title || 'Financial trend')
const trendTabs = computed(() => financialTrend.value?.tabs ?? [])
const trendChartConfigs = computed(() => financialTrend.value?.configs ?? {})
const selectedTrendConfig = computed<BondV2FinancialTrendConfig | null>(() => (
    trendChartConfigs.value[selectedTrendTab.value]
    ?? Object.values(trendChartConfigs.value)[0]
    ?? null
))
const hasIssuerGlanceContent = computed(() => metricItems.value.length > 0 || Boolean(selectedTrendConfig.value))
const issuerGlanceGateMessage = computed(() => `Sign up to view ${data.value.issuerGlance.title || 'Issuer at a glance'}`)
const selectedTrendChangeTone = computed(() => {
    if (!selectedTrendConfig.value) return 'neutral'
    if (selectedTrendConfig.value.changeTone) return selectedTrendConfig.value.changeTone
    if (selectedTrendConfig.value.change.includes('-') || selectedTrendConfig.value.change.includes('↓')) return 'negative'

    return 'positive'
})
const selectedDoughnutCharts = computed(() => (
    selectedTrendConfig.value?.type === 'doughnut-grid' ? selectedTrendConfig.value.charts : []
))

const trendChartAriaLabel = computed(() => {
    const chartConfig = selectedTrendConfig.value
    if (!chartConfig) return `${selectedTrendTab.value || 'Financial trend'} chart`

    const values = chartConfig.labels
        .map((label, index) => `${label}: ${formatTrendTooltipValue(chartConfig.values[index], chartConfig.unitType)}`)
        .join(', ')

    return `${selectedTrendTab.value || trendTitle.value} chart. ${values}`
})

const doughnutChartAriaLabel = (chart: any) => {
    const chartConfig = selectedTrendConfig.value
    const values = chart.labels
        .map((label: string, index: number) => `${label}: ${formatTrendTooltipValue(chart.values[index], chartConfig?.unitType)}`)
        .join(', ')

    return `${chart.title} chart. ${values}`
}

const trendTabId = (tab: string) => `issuer-glance-trend-tab-${tab.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
const trendPanelId = (tab: string) => `issuer-glance-trend-panel-${tab.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

const handleTrendTabKeydown = (event: KeyboardEvent, index: number) => {
    const lastIndex = trendTabs.value.length - 1
    let nextIndex = index

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = index === lastIndex ? 0 : index + 1
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = index === 0 ? lastIndex : index - 1
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = lastIndex
    else return

    event.preventDefault()
    selectedTrendTab.value = trendTabs.value[nextIndex]
    nextTick(() => {
        document.getElementById(trendTabId(selectedTrendTab.value))?.focus()
    })
}

const handleSignupGate = () => {
    const sectionTitle = data.value.issuerGlance.title || 'Issuer at a glance'
    emit('signup-required', {
        title: `Sign Up to View ${sectionTitle}`,
        message: `Sign up to access ${sectionTitle} and financial trend details.`,
        ctaName: 'signup-issuer-glance-bond-details'
    })
}

const formatTrendTooltipValue = (value: unknown, unitType?: string) => {
    const numericValue = Number(value)
    const displayValue = Number.isFinite(numericValue)
        ? numericValue.toLocaleString('en-IN', {
            maximumFractionDigits: 2
        })
        : String(value ?? '')

    if (unitType === 'CURRENCY') return `₹${displayValue} Cr`
    if (unitType === 'PERCENTAGE') return `${displayValue}%`
    if (unitType === 'RATIO' || unitType === 'TIMES') return `${displayValue}x`

    return displayValue
}

const trendYAxisBounds = (values: number[]) => {
    const finiteValues = values.filter((value) => Number.isFinite(value))
    if (!finiteValues.length) return { min: 0, max: 100 }

    const lowestValue = Math.min(...finiteValues, 0)
    const highestValue = Math.max(...finiteValues, 0)
    if (lowestValue === 0 && highestValue === 0) return { min: 0, max: 100 }

    const valueRange = highestValue - lowestValue
    const padding = valueRange > 0 ? valueRange * 0.22 : Math.max(Math.abs(highestValue) * 0.22, 1)

    return {
        min: lowestValue < 0 ? Math.floor(lowestValue - padding) : 0,
        max: highestValue > 0 ? Math.ceil(highestValue + padding) : 0
    }
}

const hasNegativeTrendValue = (values: number[]) => values.some((value) => Number.isFinite(value) && value < 0)
const hasMixedTrendValues = (values: number[]) => {
    return values.some((value) => Number.isFinite(value) && value < 0)
        && values.some((value) => Number.isFinite(value) && value > 0)
}

const financialTrendBarZeroLinePlugin = {
    id: 'financialTrendBarZeroLine',
    beforeDatasetsDraw(chart: Chart) {
        const chartConfig = selectedTrendConfig.value
        if (!chartConfig || chartConfig.type !== 'bar' || !hasMixedTrendValues(chartConfig.values)) return

        const yScale = chart.scales.y
        if (!yScale) return

        const zeroY = yScale.getPixelForValue(0)
        const { left, right } = chart.chartArea
        const ctx = chart.ctx

        ctx.save()
        ctx.strokeStyle = '#cbd5e1'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(left, zeroY)
        ctx.lineTo(right, zeroY)
        ctx.stroke()
        ctx.restore()
    }
}

const financialTrendValueLabelsPlugin = {
    id: 'financialTrendValueLabels',
    afterDatasetsDraw(chart: Chart) {
        const chartConfig = selectedTrendConfig.value
        if (!chartConfig || chartConfig.type === 'doughnut-grid') return

        const meta = chart.getDatasetMeta(0)
        const ctx = chart.ctx

        ctx.save()
        ctx.fillStyle = '#1b2559'
        ctx.font = '700 11px Inter, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'

        for (const [index, point] of meta.data.entries()) {
            const value = chartConfig.values[index]
            if (value == null) continue

            const label = formatTrendTooltipValue(value, chartConfig.unitType)
            const halfLabelWidth = ctx.measureText(label).width / 2
            const labelX = Math.min(
                Math.max(point.x, halfLabelWidth + 4),
                chart.width - halfLabelWidth - 4
            )
            const isNegativeValue = value < 0
            const pointWithBase = point as typeof point & { base?: number }
            const labelAnchorY = isNegativeValue && chartConfig.type === 'bar'
                ? Math.max(point.y, pointWithBase.base ?? point.y)
                : point.y
            const labelY = isNegativeValue
                ? Math.min(labelAnchorY + 8, chart.height - 14)
                : Math.max(labelAnchorY - 6, chart.chartArea.top + 12)

            ctx.fillStyle = isNegativeValue ? '#b91c1c' : '#1b2559'
            ctx.textBaseline = isNegativeValue ? 'top' : 'bottom'
            ctx.fillText(label, labelX, labelY)
        }

        ctx.restore()
    }
}

const destroyTrendChart = () => {
    for (const chart of trendCharts) {
        chart.destroy()
    }
    trendCharts = []
}

const setDoughnutCanvas = (element: unknown, index: number) => {
    if (element instanceof HTMLCanvasElement) {
        trendDoughnutCanvases.value[index] = element
    }
}

const initDoughnutCharts = () => {
    const chartConfig = selectedTrendConfig.value

    if (!chartConfig) return
    if (chartConfig.type !== 'doughnut-grid') return

    destroyTrendChart()
    trendDoughnutCanvases.value = trendDoughnutCanvases.value.slice(0, chartConfig.charts.length)

    for (const [index, chart] of chartConfig.charts.entries()) {
        const canvas = trendDoughnutCanvases.value[index]
        const ctx = canvas?.getContext('2d')

        if (!ctx) continue

        trendCharts.push(new Chart(ctx, {
            type: chart.chartType ?? 'doughnut',
            data: {
                labels: chart.labels,
                datasets: [
                    {
                        data: chart.values,
                        backgroundColor: chart.legend.map((item) => item.color),
                        borderWidth: 0,
                        hoverOffset: 0
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                cutout: chart.chartType === 'pie' ? 0 : '70%',
                rotation: -55,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            title: () => '',
                            label: (context: any) => {
                                const rawLabel = chart.tooltipLabels?.[context.dataIndex] ?? context.label
                                const label = rawLabel ? `${rawLabel}: ` : ''
                                return `${label}${formatTrendTooltipValue(context.raw, chartConfig.unitType)}`
                            }
                        }
                    },
                    datalabels: { display: false }
                } as any
            }
        } as any))
    }
}

const initTrendChart = () => {
    const canvas = trendChartCanvas.value
    const ctx = canvas?.getContext('2d')
    const chartConfig = selectedTrendConfig.value

    if (!chartConfig) return
    if (chartConfig.type === 'doughnut-grid') {
        initDoughnutCharts()
        return
    }

    if (!ctx) return

    destroyTrendChart()

    const yAxisBounds = trendYAxisBounds(chartConfig.values)
    const hasNegativeValues = hasNegativeTrendValue(chartConfig.values)

    trendCharts.push(new Chart(ctx, {
        type: chartConfig.type,
        data: {
            labels: chartConfig.labels,
            datasets: [
                {
                    data: chartConfig.values,
                    backgroundColor: chartConfig.type === 'bar'
                        ? chartConfig.values.map((value) => value < 0 ? '#dc2626' : '#7c3aed')
                        : 'transparent',
                    borderColor: '#0027af',
                    borderRadius: chartConfig.type === 'bar'
                        ? (context: { raw: unknown }) => {
                            const value = Number(context.raw)

                            return value < 0
                                ? {
                                    topLeft: 0,
                                    topRight: 0,
                                    bottomLeft: 4,
                                    bottomRight: 4
                                }
                                : {
                                    topLeft: 4,
                                    topRight: 4,
                                    bottomLeft: 0,
                                    bottomRight: 0
                                }
                        }
                        : 0,
                    borderSkipped: false,
                    borderWidth: chartConfig.type === 'line' ? 3 : 0,
                    barPercentage: 1,
                    categoryPercentage: 0.82,
                    fill: false,
                    minBarLength: 18,
                    pointBackgroundColor: '#fff',
                    pointBorderColor: '#0027af',
                    pointBorderWidth: 2,
                    pointRadius: chartConfig.type === 'line' ? 4 : 0,
                    tension: 0.4
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            events: [],
            layout: {
                padding: {
                    top: 18,
                    right: 0,
                    bottom: hasNegativeValues ? 18 : 0,
                    left: 0
                }
            },
            plugins: {
                legend: { display: false },
                datalabels: { display: false }
            } as any,
            scales: {
                x: {
                    border: { display: false },
                    grid: { display: false },
                    ticks: {
                        color: '#424d71',
                        font: {
                            size: 10,
                            weight: 700,
                            family: 'Inter'
                        },
                        padding: 8
                    }
                },
                y: {
                    min: yAxisBounds.min,
                    max: yAxisBounds.max,
                    border: { display: false },
                    grid: { display: false },
                    ticks: { display: false }
                }
            }
        },
        plugins: [financialTrendBarZeroLinePlugin, financialTrendValueLabelsPlugin]
    } as any))
}

watch(trendTabs, (tabs) => {
    if (!tabs.length) return
    if (!tabs.includes(selectedTrendTab.value)) {
        selectedTrendTab.value = tabs[0]
    }
}, { immediate: true })

watch(isOpen, (open) => {
    if (!open) {
        destroyTrendChart()
        return
    }

    nextTick(() => {
        initTrendChart()
    })
}, { immediate: true })

watch(selectedTrendTab, () => {
    if (!isOpen.value) return

    nextTick(() => {
        initTrendChart()
    })
})

watch(trendChartConfigs, () => {
    if (!isOpen.value) return

    nextTick(() => {
        initTrendChart()
    })
})

onBeforeUnmount(() => {
    destroyTrendChart()
})
</script>

<style scoped>
.issuer-glance {
    border: none;
    border-radius: var(--bond-v2-radius, 7px);
    background: #F9FBFF;
    overflow: hidden;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    container-type: inline-size;
}

.issuer-glance__header {
    width: 100%;
    border: none;
    background: #F9FBFF;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0;
    cursor: pointer;
}

.issuer-glance__heading {
    margin: 0;
}

.issuer-glance__title,
.trend__title {
    margin: 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 15px;
    font-weight: 800;
}

.issuer-glance__header i {
    color: var(--bond-v2-primary, var(--color-primary-40));
    font-size: 18px;
    flex: 0 0 auto;
}

.issuer-glance__body {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    min-width: 0;
    margin-top: 16px;
}

.glance-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    min-width: 0;
    padding: 0;
}

.glance-tile {
    height: 140px;
    border: none;
    border-radius: 7px;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 22px 12px;
    text-align: center;
    box-sizing: border-box;
}

.glance-tile__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--bond-v2-soft, var(--color-primary-90));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.glance-tile__icon img {
    width: 24px;
    height: 24px;
    display: block;
}

.glance-tile strong {
    color: #1b2559;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 15px;
}

.glance-tile span {
    color: #5b6684;
    text-align: center;
    font-size: 13px;
    font-weight: 500;
    line-height: 17px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.glance-info-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    padding: 0;
}

.glance-tile__info-icon {
    color: #0027af;
    font-size: 14px;
}

.trend {
    background: #fff;
    border-radius: 5px;
    padding: 16px 12px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.trend-tabs {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
}

.trend-tabs button {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #f8fafc;
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-weight: 500;
    line-height: normal;
    padding: 9px 12px;
    cursor: pointer;
}

.trend-tabs button.active {
    color: #0027af;
    border-color: #0027af;
}

.trend__change {
    margin: 12px 0 0;
    color: #008940;
    font-size: 12px;
    font-weight: 800;
}

.trend__change--negative {
    color: #a31616;
}

.trend__change--neutral {
    color: #5b6684;
}

.chart {
    height: 210px;
    padding: 24px 12px 0;
}

.chart canvas {
    width: 100%;
    height: 100%;
    display: block;
}

.doughnut-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    margin-top: 18px;
}

.doughnut-card {
    min-height: 334px;
    border-radius: 7px;
    background: #fff;
    padding: 18px 12px 14px;
    box-sizing: border-box;
}

.doughnut-card__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: #111827;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
}

.doughnut-card__info-button {
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 0 0 auto;
}

.doughnut-card__info-button img {
    width: 24px;
    height: 24px;
    display: block;
}

.doughnut-card__chart {
    width: 100%;
    height: 190px;
    margin-top: 20px;
}

.doughnut-card__chart canvas {
    width: 100%;
    height: 100%;
    display: block;
}

.trend-legend {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    column-gap: 12px;
    row-gap: 8px;
    margin-top: 12px;
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
}

.trend-legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}

.trend-legend i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: 0 0 8px;
}

@media (max-width: 900px) {
    .glance-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .doughnut-grid {
        grid-template-columns: 1fr;
    }
}

@container (max-width: 760px) {
    .glance-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .doughnut-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 560px) {
    .issuer-glance {
        border-radius: 0;
        padding: 12px 16px;
        box-sizing: border-box;
    }

    .glance-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        padding: 0;
    }

    .glance-tile {
        min-width: 0;
        height: 124px;
        padding: 12px 8px;
    }

    .glance-tile__icon {
        width: 40px;
        height: 40px;
    }

    .glance-tile__icon img {
        width: 21px;
        height: 21px;
    }

    .glance-tile strong {
        font-size: 14px;
        line-height: 15px;
    }

    .glance-tile span {
        font-size: 11px;
        line-height: 14px;
    }

    .trend {
        padding: 14px 10px;
    }

    .trend-tabs {
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 6px;
        scrollbar-width: none;
    }

    .trend-tabs::-webkit-scrollbar {
        display: none;
    }

    .trend-tabs button {
        flex: 0 0 auto;
        padding: 8px 10px;
    }

    .chart {
        height: 190px;
        padding: 18px 0 0;
    }

    .doughnut-card {
        min-height: 300px;
        padding: 16px 10px 12px;
    }

    .doughnut-card__title {
        font-size: 13px;
        line-height: 18px;
    }

    .doughnut-card__chart {
        height: 176px;
        margin-top: 16px;
    }

    .trend-legend {
        font-size: 11px;
        line-height: 16px;
    }

    .trend-legend span {
        white-space: normal;
    }
}
</style>

<style>
.issuer-glance-tooltip .v-overlay__content,
.issuer-glance-tooltip.v-tooltip .v-overlay__content {
    min-width: 180px !important;
    max-width: 280px !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    text-align: left !important;
    font-size: 12px !important;
}
</style>
